import { type ComponentProps, type ComponentType, memo, type ReactNode, type RefObject } from 'react';

import { cn } from '@air/utils-tailwind';

export type UploadZoneOverlayProps = Omit<ComponentProps<'div'>, 'ref' | 'title'> & {
  description: ReactNode;
  icon: ComponentType<{ className?: string }>;
  /** Whether the cursor is over this zone, which is what the solid outline announces. */
  isActive: boolean;
  /** Omitted from the native props above, which would otherwise intersect this down to `string`. */
  title: ReactNode;
  /** Named rather than forwarded so the zones can keep the refs they hit-test against. */
  zoneRef?: RefObject<HTMLDivElement>;
};

/** The glass panel every drop zone is drawn as. Callers own their own position and size. */
export const UploadZoneOverlay = memo(
  ({ className, description, icon: Icon, isActive, title, zoneRef, ...rest }: UploadZoneOverlayProps) => (
    <div
      {...rest}
      ref={zoneRef}
      className={cn(
        'flex flex-col items-center justify-center rounded-12 ads:bg-glass/80 bg-gray-1/80 backdrop-blur-[17.5px]',
        isActive
          ? 'outline ads:outline-primary outline-2 outline-blue-9'
          : 'outline-dashed ads:outline-strong outline-1 outline-gray-7',
        className,
      )}
    >
      <div className="flex flex-col items-center gap-2">
        <Icon className="size-6" />
        <div className="flex flex-col items-center gap-1 text-center">
          <h2 className="font-medium ads:text-primary text-16 text-gray-12">{title}</h2>
          <p className="ads:text-metadata text-12 text-gray-10">{description}</p>
        </div>
      </div>
    </div>
  ),
);

UploadZoneOverlay.displayName = 'UploadZoneOverlay';
